<template>
  <div
    ref="dropZone"
    id="dropZone"
    class="container"
    :class="{ 'is-dragover': onDragOver && !this.fileUploadModal }"
  >
    <!-- Content header -->
    <div class="columns">
      <div class="column is-3">
        <h3 class="title is-3">{{ pageTitle }}</h3>
      </div>
      <div class="column">
        <search-content :includeShared="hasTeam" />
      </div>
      <div class="column is-2 has-text-right">
        <button
          type="button"
          class="button is-success is-outlined is-light"
          @click="fileUploadModal = true"
          v-if="currentFolderId !== 'bookmarks'"
        >
          <span class="icon">
            <i class="fas fa-plus"></i>
          </span>
          <span>Add Document</span>
        </button>
      </div>
    </div>
    <!-- END Content header -->
    <!-- Content container -->
    <div class="columns">
      <!-- Menu Column -->
      <div class="column is-3 explorer-container is-hidden-touch">
        <aside class="main-folders-container">
          <drop
            class="main-folder is-flex is-justify-content-space-between"
            :class="{
              'is-active has-background-success-light':
                currentFolderId === 'my-stuff',
              'is-expanded': !isMyStuffCollapsed
            }"
            @drop="onItemDrop($event)"
          >
            <div class="main-label">
              <a
                @click="isMyStuffCollapsed = !isMyStuffCollapsed"
                v-if="folders.length"
              >
                <span class="icon is-small">
                  <i class="fas fa-chevron-down" v-if="isMyStuffCollapsed"></i>
                  <i class="fas fa-minus" v-else></i>
                </span>
              </a>
              <router-link
                :to="`/content/my-stuff`"
                class="no-drag"
                :class="{ 'has-text-success': currentFolderId === 'my-stuff' }"
              >
                <span class="icon">
                  <i
                    class="fas fa-folder-open"
                    v-if="currentFolderId === 'my-stuff'"
                  ></i>
                  <i class="fas fa-folder" v-else></i>
                </span>
                <span class="ml-1">My Stuff</span>
              </router-link>
            </div>
            <div class="main-label mainicons">
              <a
                class="tooltip is-tooltip-bottom has-text-info"
                data-tooltip="New Folder"
                @click="
                  updateFolderModal({
                    opened: true,
                    isUpdate: false
                  })
                "
              >
                <span class="icon">
                  <i class="fas fa-plus-circle"></i>
                </span>
              </a>
            </div>
          </drop>
          <div class="folders-container" v-if="!isMyStuffCollapsed">
            <drop
              v-for="folder in folders"
              :key="folder._id"
              class="folder"
              :class="{ 'is-expanded': !folder.isCollapsed }"
              @drop="onItemDrop($event, folder)"
            >
              <div
                class="folder-label is-flex is-justify-content-space-between is-white-space-nowrap tooltip"
                :class="{
                  'has-background-success-light is-active':
                    currentFolderId === folder._id
                }"
                :data-tooltip="folder.name"
              >
                <div class="main-label is-ellipsis">
                  <a
                    @click="folder.isCollapsed = !folder.isCollapsed"
                    v-if="folder.folders.length"
                  >
                    <span class="icon is-small">
                      <i
                        class="fas fa-chevron-down"
                        v-if="folder.isCollapsed"
                      ></i>
                      <i class="fas fa-minus" v-else></i>
                    </span>
                  </a>
                  <router-link
                    :to="`/content/${folder._id}`"
                    class="no-drag"
                    :class="{
                      'has-text-success': currentFolderId === folder._id,
                      'pl-4': !folder.folders.length
                    }"
                  >
                    <span class="icon">
                      <i
                        class="fas fa-folder-open"
                        v-if="folder._id === currentFolderId"
                      ></i>
                      <i class="fas fa-folder" v-else></i>
                    </span>
                    <span class="ml-1">{{ folder.name }}</span>
                  </router-link>
                </div>
                <div class="main-label icons">
                  <a
                    class="tooltip is-tooltip-bottom has-text-info"
                    data-tooltip="New Folder"
                    @click="
                      updateFolderModal({
                        opened: true,
                        isUpdate: false,
                        folder
                      })
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-plus-circle"></i>
                    </span>
                  </a>
                  <a
                    class="tooltip is-tooltip-bottom has-text-info"
                    data-tooltip="Rename"
                    @click="
                      updateFolderModal({
                        opened: true,
                        isUpdate: true,
                        folder
                      })
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-edit"></i>
                    </span>
                  </a>
                  <a
                    class="tooltip is-tooltip-bottom has-text-danger"
                    data-tooltip="Delete"
                    @click="
                      updateFolderModal({ opened: true, remove: true, folder })
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-trash-alt"></i>
                    </span>
                  </a>
                </div>
              </div>
              <div
                v-if="folder.folders.length && !folder.isCollapsed"
                class="subfolders-container"
              >
                <drop
                  v-for="subfolder in folder.folders"
                  :key="subfolder._id"
                  class="subfolder"
                  @drop="onItemDrop($event, subfolder)"
                >
                  <div
                    class="subfolder-label is-flex is-justify-content-space-between is-white-space-nowrap tooltip"
                    :class="
                      currentFolderId === subfolder._id
                        ? ['has-background-success-light', 'is-active']
                        : ''
                    "
                    :data-tooltip="subfolder.name"
                  >
                    <div class="main-label is-ellipsis">
                      <router-link
                        :to="`/content/${subfolder._id}`"
                        class="no-drag"
                        :class="{
                          'has-text-success': currentFolderId === subfolder._id
                        }"
                      >
                        <span class="icon">
                          <i
                            class="fas fa-folder-open"
                            v-if="subfolder._id === currentFolderId"
                          ></i>
                          <i class="fas fa-folder" v-else></i>
                        </span>
                        <span class="ml-1">{{ subfolder.name }}</span>
                      </router-link>
                    </div>
                    <div class="main-label subicons">
                      <a
                        class="tooltip is-tooltip-bottom has-text-info"
                        data-tooltip="Rename"
                        @click="
                          updateFolderModal({
                            opened: true,
                            isUpdate: true,
                            folder: subfolder
                          })
                        "
                      >
                        <span class="icon">
                          <i class="fas fa-edit"></i>
                        </span>
                      </a>
                      <a
                        class="tooltip is-tooltip-bottom has-text-danger"
                        data-tooltip="Delete"
                        @click="
                          updateFolderModal({
                            opened: true,
                            remove: true,
                            folder: subfolder
                          })
                        "
                      >
                        <span class="icon">
                          <i class="fas fa-trash-alt"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                </drop>
              </div>
            </drop>
          </div>
          <!--
            Team Documents Menu
          -->
          <drop
            class="main-folder is-flex is-justify-content-space-between"
            :class="{
              'is-active has-background-success-light':
                currentFolderId === 'team',
              'is-expanded': !isTeamCollapsed
            }"
            @drop="onItemDrop($event, teamMainFolder)"
            v-if="hasTeam"
          >
            <div class="main-label">
              <a
                @click="isTeamCollapsed = !isTeamCollapsed"
                v-if="teamFolders.length"
              >
                <span class="icon is-small">
                  <i class="fas fa-chevron-down" v-if="isTeamCollapsed"></i>
                  <i class="fas fa-minus" v-else></i>
                </span>
              </a>
              <router-link
                :to="`/content/team`"
                class="no-drag"
                :class="{ 'has-text-success': currentFolderId === 'team' }"
              >
                <span class="icon">
                  <i
                    class="fas fa-folder-open"
                    v-if="currentFolderId === 'team'"
                  ></i>
                  <i class="fas fa-folder" v-else></i>
                </span>
                <span class="ml-1">Team Documents</span>
              </router-link>
            </div>
            <div class="main-label mainicons">
              <a
                class="tooltip is-tooltip-bottom has-text-info"
                data-tooltip="New Folder"
                @click="
                  updateFolderModal({
                    opened: true,
                    isUpdate: false,
                    isShared: true
                  })
                "
              >
                <span class="icon">
                  <i class="fas fa-plus-circle"></i>
                </span>
              </a>
            </div>
          </drop>
          <div class="folders-container" v-if="!isTeamCollapsed">
            <drop
              v-for="folder in teamFolders"
              :key="folder._id"
              class="folder"
              :class="{ 'is-expanded': !folder.isCollapsed }"
              @drop="onItemDrop($event, folder)"
            >
              <div
                class="folder-label is-flex is-justify-content-space-between is-white-space-nowrap tooltip"
                :class="{
                  'has-background-success-light is-active':
                    currentFolderId === folder._id
                }"
                :data-tooltip="folder.name"
              >
                <div class="main-label is-ellipsis">
                  <a
                    @click="folder.isCollapsed = !folder.isCollapsed"
                    v-if="folder.folders.length"
                  >
                    <span class="icon is-small">
                      <i
                        class="fas fa-chevron-down"
                        v-if="folder.isCollapsed"
                      ></i>
                      <i class="fas fa-minus" v-else></i>
                    </span>
                  </a>
                  <router-link
                    :to="`/content/${folder._id}`"
                    class="no-drag"
                    :class="{
                      'has-text-success': currentFolderId === folder._id,
                      'pl-4': !folder.folders.length
                    }"
                  >
                    <span class="icon">
                      <i
                        class="fas fa-folder-open"
                        v-if="folder._id === currentFolderId"
                      ></i>
                      <i class="fas fa-folder" v-else></i>
                    </span>
                    <span class="ml-1">{{ folder.name }}</span>
                  </router-link>
                </div>
                <div class="main-label icons">
                  <a
                    class="tooltip is-tooltip-bottom has-text-info"
                    data-tooltip="New Folder"
                    @click="
                      updateFolderModal({
                        opened: true,
                        isUpdate: false,
                        folder,
                        isShared: true
                      })
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-plus-circle"></i>
                    </span>
                  </a>
                  <a
                    class="tooltip is-tooltip-bottom has-text-info"
                    data-tooltip="Rename"
                    @click="
                      updateFolderModal({
                        opened: true,
                        isUpdate: true,
                        folder,
                        isShared: true
                      })
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-edit"></i>
                    </span>
                  </a>
                  <a
                    class="tooltip is-tooltip-bottom has-text-danger"
                    data-tooltip="Delete"
                    @click="
                      updateFolderModal({
                        opened: true,
                        remove: true,
                        folder,
                        isShared: true
                      })
                    "
                  >
                    <span class="icon">
                      <i class="fas fa-trash-alt"></i>
                    </span>
                  </a>
                </div>
              </div>
              <div
                v-if="folder.folders.length && !folder.isCollapsed"
                class="subfolders-container"
              >
                <drop
                  v-for="subfolder in folder.folders"
                  :key="subfolder._id"
                  class="subfolder"
                  @drop="onItemDrop($event, subfolder)"
                >
                  <div
                    class="subfolder-label is-flex is-justify-content-space-between is-white-space-nowrap tooltip"
                    :class="
                      currentFolderId === subfolder._id
                        ? ['has-background-success-light', 'is-active']
                        : ''
                    "
                    :data-tooltip="subfolder.name"
                  >
                    <div class="main-label is-ellipsis">
                      <router-link
                        :to="`/content/${subfolder._id}`"
                        class="no-drag"
                        :class="{
                          'has-text-success': currentFolderId === subfolder._id
                        }"
                      >
                        <span class="icon">
                          <i
                            class="fas fa-folder-open"
                            v-if="subfolder._id === currentFolderId"
                          ></i>
                          <i class="fas fa-folder" v-else></i>
                        </span>
                        <span class="ml-1">{{ subfolder.name }}</span>
                      </router-link>
                    </div>
                    <div class="main-label subicons">
                      <a
                        class="tooltip is-tooltip-bottom has-text-info"
                        data-tooltip="Rename"
                        @click="
                          updateFolderModal({
                            opened: true,
                            isUpdate: true,
                            folder: subfolder,
                            isShared: true
                          })
                        "
                      >
                        <span class="icon">
                          <i class="fas fa-edit"></i>
                        </span>
                      </a>
                      <a
                        class="tooltip is-tooltip-bottom has-text-danger"
                        data-tooltip="Delete"
                        @click="
                          updateFolderModal({
                            opened: true,
                            remove: true,
                            folder: subfolder,
                            isShared: true
                          })
                        "
                      >
                        <span class="icon">
                          <i class="fas fa-trash-alt"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                </drop>
              </div>
            </drop>
          </div>
          <!--
            END Team Documents Menu
          -->
          <div
            class="bookmarks-folder is-flex is-justify-content-space-between"
            :class="
              currentFolderId === 'bookmarks'
                ? ['is-active', 'has-background-success-light']
                : ''
            "
          >
            <div class="main-label">
              <router-link
                :to="`/content/bookmarks`"
                class="no-drag"
                :class="{ 'has-text-success': currentFolderId === 'bookmarks' }"
              >
                <span class="icon">
                  <i
                    class="fas fa-folder-open"
                    v-if="currentFolderId === 'bookmarks'"
                  ></i>
                  <i class="fas fa-folder" v-else></i>
                </span>
                <span class="ml-1">Bookmarks</span>
              </router-link>
            </div>
          </div>
        </aside>
      </div>
      <!-- END Menu Column -->
      <!-- Documents Listing -->
      <div class="column is-9">
        <div class="wrapper">
          <!-- Breadcrumbs -->
          <div
            class="breadcrumb is-hidden-desktop"
            aria-label="breadcrumb"
            v-if="currentFolderId !== 'bookmark'"
          >
            <ul>
              <li>
                <router-link
                  to="/content"
                  :class="{ 'is-active': currentFolderId === 'my-stuff' }"
                >
                  <span class="icon is-small">
                    <i
                      class="fas fa-folder-open"
                      aria-hidden="true"
                      v-if="currentFolderId === 'my-stuff'"
                    ></i>
                    <i class="fas fa-folder" aria-hidden="true" v-else></i>
                  </span>
                  <span>My Stuff</span>
                </router-link>
              </li>
              <li v-if="cParent">
                <router-link :to="`/content/${currentFolder.parent._id}`">
                  <span class="icon is-small">
                    <i class="fas fa-folder-open" aria-hidden="true"></i>
                  </span>
                  <span>{{ currentFolder.parent.name }}</span>
                </router-link>
              </li>
              <li
                class="is-active"
                v-if="currentFolder && currentFolderId !== 'my-stuff'"
              >
                <a href="javascript:void(0)" aria-current="page">
                  <span class="icon is-small">
                    <i class="fas fa-folder-open" aria-hidden="true"></i>
                  </span>
                  <span>{{ currentFolder.name }}</span>
                </a>
              </li>
            </ul>
          </div>
          <folders
            v-if="currentFolderId !== 'bookmarks'"
            :folderList="cFolders"
            :isShared="isShared"
            @updateFolder="updateFolderModal"
          />
          <hr v-if="currentFolderId !== 'bookmarks' && cFolders.length" />
          <my-documents
            v-if="currentFolderId !== 'bookmarks' && documentsReady"
            :documentList="documents"
            :onDelete="openConfirmDelete"
            :isShared="isShared"
            @updateDocument="openFileUploadModal"
          />
          <bookmarks
            :model="bookmarks"
            v-if="currentFolderId === 'bookmarks'"
          />
          <!-- <pagination
            :current="pagination.current"
            :total="pagination.total"
            :itemsPerPage="pagination.itemsPerPage"
            :onChange="fetch"
          >
          </pagination> -->
        </div>
      </div>
      <!-- END Document Listing -->
    </div>
    <!-- END Content container -->
    <confirmation
      @confirmDelete="removeDocument"
      @close="showConfirm = false"
      :modal="showConfirm"
      :toDelete="documentToDelete"
    ></confirmation>
    <!-- New Folder Modal -->
    <sdv-dialog
      :open="folderModal.opened"
      @close="updateFolderModal({ opened: false })"
    >
      <template #header>
        <p class="modal-card-title">
          <span class="icon">
            <i class="fas fa-folder"></i>
          </span>
          <span v-if="folderModal.remove" class="ml-2">Delete Folder</span>
          <span v-else-if="folderModal.isUpdate" class="ml-2"
            >Update Folder</span
          >
          <span v-else class="ml-2">Create Folder</span>
        </p>
      </template>
      <template #footer>
        <button @click="updateFolderModal({ opened: false })" class="button">
          Close
        </button>
        <button
          @click="updateSelectedFolder"
          class="button is-info"
          :class="folderModal.remove ? 'is-danger' : 'is-info'"
        >
          <span v-if="folderModal.remove">Delete</span>
          <span v-else-if="folderModal.isUpdate">Update</span>
          <span v-else>Create</span>
        </button>
      </template>
      <template>
        <div v-if="folderModal.remove" class="py-5">
          <p class="is-size-5 has-text-weight-semibold mb-4">
            Are you sure you wish to Delete this folder and all of its contents?
          </p>
          <div class="subtitle">
            This action is irreversible!
          </div>
        </div>
        <div v-else class="py-5">
          <div class="field">
            <label class="label" for="folder">Enter folder name</label>
            <p class="control has-icons-left has-icons-right">
              <input
                ref="createFolderBtn"
                id="folder"
                class="input"
                type="text"
                :placeholder="
                  folderModal.isUpdate ? 'Update Folder' : 'New Folder'
                "
              />
              <span class="icon is-small is-left">
                <i class="fas fa-folder"></i>
              </span>
            </p>
          </div>
        </div>
      </template>
    </sdv-dialog>
    <!-- END New Folder Modal -->
    <!-- Upload Modal -->
    <sdv-dialog :open="fileUploadModal" @close="closeFileUploadModal">
      <template #header>
        <p class="modal-card-title">Upload Document</p>
      </template>
      <template #footer>
        <button @click="closeFileUploadModal" class="button is-info">
          Close
        </button>
      </template>
      <template>
        <div class="columns">
          <div class="column is-full">
            <upload
              :folder="currentFolderId"
              :isShared="isShared"
              :documentId="updateDocumentId"
              :dropedFile="dropedFile"
              @finished="closeFileUploadModal"
            />
          </div>
        </div>
      </template>
    </sdv-dialog>
    <!-- END Upload Modal -->
  </div>
</template>
<script>
import { checkUserDocumentsTotalStorage } from './../../utils/documents';
// import Pagination from 'vue-2-bulma-pagination';
import { mapState, mapActions, mapGetters } from 'vuex';
import { getFolder } from '@/utils/general';
import { Drop } from 'vue-easy-dnd';
import DragDropMixin from '@/components/mixins/content/DragDropMixin';

const paginationDefaults = {
  current: 1,
  total: 0,
  itemsPerPage: 10
};

export default {
  name: 'Content',
  components: {
    Drop,
    Upload: () => import('@/components/UploadComponent'),
    Confirmation: () => import('@/components/ConfirmationDelete'),
    SdvDialog: () => import('@/components/shared/SdvDialog'),
    // Pagination: () => import('vue-2-bulma-pagination'),
    Bookmarks: () => import('@/components/content/Bookmarks'),
    MyDocuments: () => import('@/components/content/MyDocuments'),
    Folders: () => import('@/components/content/Folders'),
    SearchContent: () => import('@/components/shared/SearchContent')
  },
  mixins: [DragDropMixin],
  data() {
    return {
      documentsReady: false,
      bookmarks: [],
      showConfirm: false,
      documentToDelete: {},
      pagination: Object.assign({}, paginationDefaults),
      fileUploadModal: false,
      folderModal: {
        id: null,
        opened: false,
        isUpdate: false,
        isShared: false
      },
      currentFolderId: this.$route.params?.folder || 'my-stuff',
      updateDocumentId: null,
      dragCounter: 0,
      onDragOver: false,
      dropedFile: null,
      isMyStuffCollapsed: false,
      isTeamCollapsed: true
    };
  },
  watch: {
    $route() {
      this.$store.commit('content/RESET_SEARCH_RESULTS');
      this.currentFolderId = this.$route.params?.folder || 'my-stuff';
      this.pagination = Object.assign({}, paginationDefaults);
      this.documentsReady = false;
      this.getData();
      if (this.currentFolderId === 'my-stuff') {
        this.isMyStuffCollapsed = false;
      } else if (this.currentFolderId === 'team') {
        this.isTeamCollapsed = false;
      } else if (this.currentFolder) {
        this.currentFolder.isCollapsed = false;
      }
    }
  },
  computed: {
    ...mapGetters('content', ['getChildFolders']),
    ...mapState(`content`, [
      `folders`,
      `mainFolder`,
      `teamFolders`,
      `teamMainFolder`,
      `searchResults`
    ]),
    pageTitle() {
      return this.currentFolderId === 'bookmarks' ? 'Bookmarks' : 'Content';
    },
    cParent() {
      return this.currentFolder?.parent || null;
    },
    cFolders() {
      if (this.searchResults) {
        return this.searchResults.folders;
      }
      const currentFolders =
        this.currentFolder?.folders ||
        this.getChildFolders(this.currentFolderId) ||
        [];
      return currentFolders.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },
    currentFolder() {
      switch (this.currentFolderId) {
        case `my-stuff`:
          return this.mainFolder;
        case 'team':
          return this.teamMainFolder;
      }
      let data = getFolder(this.folders, this.currentFolderId);
      if (!data.folder && this.hasTeam) {
        data = getFolder(this.teamFolders, this.currentFolderId);
      }
      return data.folder;
    },
    documents() {
      if (!this.documentsReady) {
        return [];
      }
      if (this.searchResults) {
        return this.searchResults.documents;
      }
      const documents = this.currentFolder?.documents || [];
      return documents.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },
    hasTeam() {
      let accessLevel = this.$store.getters['auth/getAccessLevel'];
      // Only if the current plan allows for more than 1 users.
      return parseInt(accessLevel?.users?.value, 10) > 1 || false;
    },
    isShared() {
      return Boolean(this.currentFolder?.isShared);
    }
  },
  beforeMount() {
    this.$store.commit('content/RESET_SEARCH_RESULTS');
  },
  mounted() {
    this.getData();
    this.setListeners();
  },
  beforeDestroy() {
    this.setListeners({ remove: true });
  },
  methods: {
    ...mapActions(`content`, [
      `requestFolders`,
      `requestDocuments`,
      `createFolder`,
      `updateFolder`,
      `deleteFolder`,
      `deleteDocument`,
      `updateDocument`
    ]),
    async getData(page) {
      switch (this.currentFolderId) {
        case 'bookmarks':
          this.getBookmarks(page);
          break;
        default: {
          try {
            if (!this.folders.length) {
              await this.requestFolders();
            }
            if (this.hasTeam && !this.teamFolders.length) {
              await this.requestFolders({ isShared: true });
            }
            if (this.currentFolder && !this.currentFolder?.documents?.length) {
              await this.getDocuments(page);
            }
            this.documentsReady = true;
          } catch (error) {
            this.$notify({
              group: 'alert',
              title: 'Something went wrong!',
              text: 'Sorry! We were unable to get your data.',
              type: 'error'
            });
            return;
          }
          break;
        }
      }
    },
    async updateSelectedFolder() {
      try {
        this.documentsReady = false;
        if (this.folderModal.remove) {
          await this.deleteFolder({ id: this.folderModal.id });
          // TODO: the next line might be unnecessary as we don't
          // need to change the route after deting a fodler.
          // this.$router.push({ name: `content` });
        } else {
          const folderName = this.$refs.createFolderBtn.value;
          if (!folderName) {
            return this.$notify({
              group: 'alert',
              title: 'Empty Folder name!',
              text: 'Please fill in the folder name',
              type: 'error'
            });
          }
          if (this.folderModal.isUpdate) {
            const params = {
              name: folderName,
              id: this.folderModal.id,
              isShared: this.folderModal.isShared
            };
            await this.updateFolder(params);
          } else {
            const params = {
              name: folderName,
              parent: this.folderModal.id,
              isShared: this.folderModal.isShared
            };
            const { data } = await this.createFolder(params);
            this.$router.push(`/content/${data.newFolder._id}`);
          }
        }
        // Get the documents in the current folder
        this.$nextTick(async () => {
          await this.getDocuments();
          this.documentsReady = true;
        });
      } catch (error) {
        const action = this.folderModal.remove
          ? `delete`
          : this.folderModal.isUpdate
          ? `update`
          : `create`;
        this.$notify({
          group: 'alert',
          title: 'Something went wrong!',
          text: `Sorry! We were unable to ${action} folder.`,
          type: 'error'
        });
      } finally {
        this.updateFolderModal({ opened: false });
      }
    },
    async getDocuments(page) {
      page = page || 1;
      await this.requestDocuments({
        folderId: this.currentFolderId,
        isShared: this.isShared
      });
      this.pagination.total = this.currentFolder.total;
      this.pagination.current = page;
    },
    async removeDocument() {
      const docName = this.documentToDelete.name;
      try {
        const id = this.documentToDelete._id;
        const success = await this.deleteDocument({ id });
        if (success !== true) {
          throw new Error('Unable to delete document.');
        }
        this.$notify({
          group: 'alert',
          title: 'Deleted',
          text: `${docName} has been deleted.`,
          type: 'success'
        });
        this.showConfirm = false;
        this.documentToDelete = {};
        await checkUserDocumentsTotalStorage();
      } catch (error) {
        console.info('ERROR: ', error);
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text:
            'We were unable to delete your document. Please try again later.',
          type: 'error'
        });
      }
    },
    getBookmarks(page) {
      page = page || 1;
      this.$axios
        .get(`${this.$API.library}?limit=10&page=${page}`)
        .then(res => {
          this.bookmarks = res.data.bookmarks;
          this.pagination.current = page;
          this.pagination.total = res.data.meta?.total || this.bookmarks.length;
        })
        .catch(e => {
          console.log('Error: ', e);
          this.$notify({
            group: 'alert',
            title: 'Something went wrong!',
            text: 'Sorry! We were unable to get your bookmarks.',
            type: 'error'
          });
        });
    },
    openConfirmDelete(document) {
      this.documentToDelete = document;
      this.showConfirm = true;
    },
    openFileUploadModal(id) {
      this.updateDocumentId = id;
      this.fileUploadModal = true;
      this.documentsReady = false;
    },
    closeFileUploadModal() {
      this.updateDocumentId = null;
      this.fileUploadModal = false;
      this.dropedFile = null;
      this.documentsReady = true;
    },
    updateFolderModal({
      opened,
      isUpdate = false,
      folder = {},
      remove = false,
      isShared = false
    }) {
      this.folderModal = {
        opened,
        isUpdate,
        remove,
        id: folder._id ? folder._id : isShared ? 'team' : 'my-stuff',
        isShared
      };
      this.$nextTick(() => {
        if (remove) {
          return;
        }
        if (!opened) {
          this.$refs.createFolderBtn.value = '';
          return;
        }
        if (isUpdate) {
          this.$refs.createFolderBtn.value = folder.name;
        }
        this.$refs.createFolderBtn.select();
      });
    },
    onDragEvents(evt) {
      if (this.fileUploadModal) {
        return;
      }
      switch (evt.type) {
        case `drop`:
          evt.preventDefault();
          this.onDragOver = false;
          this.dragCounter = 0;
          this.dropedFile = evt.dataTransfer?.files
            ? evt.dataTransfer.files
            : evt.target.files;
          this.fileUploadModal = true;
          break;
        case `dragover`:
          evt.preventDefault();
          break;
        case `dragenter`:
          this.dragCounter++;
          this.onDragOver = true;
          break;
        case `dragleave`:
          this.dragCounter--;
          if (this.dragCounter === 0) {
            this.onDragOver = false;
          }
          break;
      }
    },
    setListeners({ remove } = {}) {
      const target = remove ? `removeEventListener` : `addEventListener`;
      [`drop`, `dragover`, `dragenter`, `dragleave`].forEach(evt => {
        this.$refs.dropZone[target](evt, this.onDragEvents, false);
      });
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/design/variables.scss';
#dropZone {
  position: relative;
  &.is-dragover:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-style: dashed;
    border-width: 2px;
    color: #fff;
    background-color: rgba($principal, 0.5);
  }
  .main-folders-container {
    .main-folder {
      padding: 0.5rem 0.3rem;
      &:hover {
        .mainicons {
          display: block;
        }
      }
      &.is-active {
        .mainicons {
          display: block !important;
        }
      }
      &.drop-in {
        border-style: dashed;
        border-width: 1.5px;
        color: $principal;
      }
      .mainicons {
        display: none;
      }
    }
    .folders-container {
      .folder {
        padding-left: 0.8rem;
        &.drop-in {
          .folder-label {
            border-style: dashed;
            border-width: 1.5px;
            color: $principal;
          }
        }
        .folder-label {
          padding: 0.5rem 0.3rem;
          &:hover {
            .icons {
              display: block;
            }
          }
          &.is-active {
            .icons {
              display: block !important;
            }
          }
          .icons {
            display: none;
          }
        }
        &.is-expanded {
          .subfolders-container {
            .subfolder {
              padding-left: 2.4rem;
            }
          }
        }
        .subfolders-container {
          .subfolder {
            padding-left: 0.8rem;
            &.drop-in {
              .subfolder-label {
                border-style: dashed;
                border-width: 1.5px;
                color: $principal;
              }
            }
            .subfolder-label {
              padding: 0.5rem 0.3rem;
              &:hover {
                .subicons {
                  display: block;
                }
              }
              &.is-active {
                .subicons {
                  display: block !important;
                }
              }
              .subicons {
                display: none;
              }
            }
          }
        }
      }
    }
    .bookmarks-folder {
      padding: 0.5rem 0.3rem;
    }
    a {
      color: $dark;
    }
    .no-drag {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      user-drag: none;
      -webkit-user-drag: none;
    }
  }
}
</style>
